import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { themedPrompt } from '@/lib/themed-dialog';
import {
  Bold, Italic, Underline, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Type, ChevronDown, Link2, Palette,
  Minus, Plus, CaseUpper, CaseLower, CaseSensitive,
} from 'lucide-react';

/**
 * TextToolbar — Iter 198 (Type Popover Polish).
 *
 * Squarespace "Type" floating popover. Dark-glass aesthetic to match the rest
 * of the builder. Applies FIELD-LEVEL typography (fontFamily/size/weight/etc.)
 * via `block.content._field_styles[field]`. Per-selection (per-word) rich
 * formatting is handled by `InlineSelectionToolbar` — the two are visually
 * consistent so they feel like one system.
 *
 * Renders via createPortal so the fixed positioning is never trapped by a
 * transformed / clipped parent.
 */
const STYLE_PRESETS = [
  { v: 'h1',      label: 'Heading 1',  size: 64, weight: 700 },
  { v: 'h2',      label: 'Heading 2',  size: 48, weight: 700 },
  { v: 'h3',      label: 'Heading 3',  size: 32, weight: 600 },
  { v: 'h4',      label: 'Heading 4',  size: 24, weight: 600 },
  { v: 'p',       label: 'Paragraph',  size: 16, weight: 400 },
  { v: 'small',   label: 'Small Text', size: 13, weight: 400 },
  { v: 'caption', label: 'Caption',    size: 12, weight: 500 },
  { v: 'quote',   label: 'Quote',      size: 22, weight: 400 },
  { v: 'mono',    label: 'Monospace',  size: 14, weight: 400 },
];

const FONTS = [
  { v: 'inherit',                 label: 'Default'              },
  { v: 'Playfair Display',        label: 'Playfair Display'     },
  { v: 'Cormorant Garamond',      label: 'Cormorant Garamond'   },
  { v: 'EB Garamond',             label: 'EB Garamond'          },
  { v: 'Libre Baskerville',       label: 'Libre Baskerville'    },
  { v: 'Lora',                    label: 'Lora'                 },
  { v: 'Merriweather',            label: 'Merriweather'         },
  { v: 'Fraunces',                label: 'Fraunces'             },
  { v: 'Bodoni Moda',             label: 'Bodoni Moda'          },
  { v: 'DM Serif Display',        label: 'DM Serif Display'     },
  { v: 'Alegreya',                label: 'Alegreya'             },
  { v: 'Crimson Text',            label: 'Crimson Text'         },
  { v: 'Spectral',                label: 'Spectral'             },
  { v: 'Cardo',                   label: 'Cardo'                },
  { v: 'PT Serif',                label: 'PT Serif'             },
  { v: 'Prata',                   label: 'Prata'                },
  { v: 'Cinzel',                  label: 'Cinzel'               },
  { v: 'Bitter',                  label: 'Bitter'               },
  { v: 'Domine',                  label: 'Domine'               },
  { v: 'Inter',                   label: 'Inter'                },
  { v: 'DM Sans',                 label: 'DM Sans'              },
  { v: 'Manrope',                 label: 'Manrope'              },
  { v: 'Archivo',                 label: 'Archivo'              },
  { v: 'Work Sans',               label: 'Work Sans'            },
  { v: 'Poppins',                 label: 'Poppins'              },
  { v: 'Open Sans',               label: 'Open Sans'            },
  { v: 'Montserrat',              label: 'Montserrat'           },
  { v: 'Karla',                   label: 'Karla'                },
  { v: 'Fira Sans',               label: 'Fira Sans'            },
  { v: 'Nunito Sans',             label: 'Nunito Sans'          },
  { v: 'Josefin Sans',            label: 'Josefin Sans'         },
  { v: 'Barlow',                  label: 'Barlow'               },
  { v: 'Rubik',                   label: 'Rubik'                },
  { v: 'Lato',                    label: 'Lato'                 },
  { v: 'Source Sans 3',           label: 'Source Sans'          },
  { v: 'Public Sans',             label: 'Public Sans'          },
  { v: 'Space Grotesk',           label: 'Space Grotesk'        },
  { v: 'Roboto',                  label: 'Roboto'               },
  { v: 'Outfit',                  label: 'Outfit'               },
  { v: 'Plus Jakarta Sans',       label: 'Plus Jakarta Sans'    },
  { v: 'Syne',                    label: 'Syne'                 },
  { v: 'Anton',                   label: 'Anton'                },
  { v: 'Archivo Black',           label: 'Archivo Black'        },
  { v: 'Bebas Neue',              label: 'Bebas Neue'           },
  { v: 'Oswald',                  label: 'Oswald'               },
  { v: 'Righteous',               label: 'Righteous'            },
  { v: 'Poiret One',              label: 'Poiret One'           },
  { v: 'Big Shoulders Display',   label: 'Big Shoulders'        },
  { v: 'Abril Fatface',           label: 'Abril Fatface'        },
  { v: 'Yeseva One',              label: 'Yeseva One'           },
  { v: 'Unbounded',               label: 'Unbounded'            },
  { v: 'Chakra Petch',            label: 'Chakra Petch'         },
  { v: 'Caveat',                  label: 'Caveat'               },
  { v: 'Dancing Script',          label: 'Dancing Script'       },
  { v: 'Great Vibes',             label: 'Great Vibes'          },
  { v: 'Sacramento',              label: 'Sacramento'           },
  { v: 'Pacifico',                label: 'Pacifico'             },
  { v: 'Kalam',                   label: 'Kalam'                },
  { v: 'Amatic SC',               label: 'Amatic SC'            },
  { v: 'JetBrains Mono',          label: 'JetBrains Mono'       },
  { v: 'Space Mono',              label: 'Space Mono'           },
  { v: 'IBM Plex Mono',           label: 'IBM Plex Mono'        },
  { v: 'Fira Code',               label: 'Fira Code'            },
  { v: 'Roboto Mono',             label: 'Roboto Mono'          },
];

const SIZE_PRESETS = [12, 14, 16, 18, 20, 24, 32, 48, 64, 96];

const COLOR_PRESETS = [
  '#FFFFFF', '#F8FAFC', '#E4E4E7', '#A1A1AA',
  '#000000', '#18181B', '#3F3F46', '#71717A',
  '#8B5CF6', '#6D28D9', '#A855F7', '#EC4899',
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
];

const TRANSFORMS = [
  { v: 'none',       label: 'aA', title: 'None'        },
  { v: 'uppercase',  label: 'AA', title: 'UPPERCASE'   },
  { v: 'lowercase',  label: 'aa', title: 'lowercase'   },
  { v: 'capitalize', label: 'Aa', title: 'Title Case'  },
];

// Dark-glass shared surface
const CARD_CLS = 'rounded-xl border border-white/[0.10] shadow-[0_16px_48px_-8px_rgba(0,0,0,0.7)] backdrop-blur-[18px] bg-[rgba(20,16,25,0.94)]';

const PopWrap = ({ children, testid, align = 'left' }) => (
  <div
    className={`absolute top-full mt-2 z-10 ${CARD_CLS} p-2 min-w-[220px] max-h-[300px] overflow-y-auto ${align === 'right' ? 'right-0' : 'left-0'}`}
    data-testid={testid}
    onClick={(e) => e.stopPropagation()}
  >{children}</div>
);

const Pill = ({ active, onClick, testid, title, children }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); onClick?.(); }}
    data-testid={testid}
    title={title}
    className={`inline-flex items-center gap-1.5 px-2 h-8 rounded-md text-[12px] font-semibold tracking-tight transition-all whitespace-nowrap ${
      active
        ? 'bg-gradient-to-b from-[#8B5CF6]/25 to-[#6D28D9]/20 text-white ring-1 ring-[#8B5CF6]/50 shadow-[0_2px_8px_rgba(139,92,246,0.3)]'
        : 'text-white/70 hover:text-white hover:bg-white/[0.06]'
    }`}
  >{children}</button>
);

const Divider = () => <div className="w-px h-5 bg-white/8 mx-0.5" />;

export default function TextToolbar({ rect, style, onChange, onClose }) {
  const [open, setOpen] = useState(null); // 'style' | 'font' | 'size' | 'color' | 'transform'
  const cardRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (cardRef.current && cardRef.current.contains(e.target)) return;
      if (e.target.closest && (e.target.closest('[data-wb-text]') || e.target.closest('[data-wb-text-toolbar]'))) return;
      onClose?.();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (!rect) return null;

  const TOOLBAR_W = 740;
  const TOOLBAR_H = 48;
  const POP_H = 240;
  const flipDown = rect.top < (POP_H + TOOLBAR_H + 20);
  const top = flipDown
    ? Math.min(window.innerHeight - TOOLBAR_H - 8, rect.bottom + 12)
    : Math.max(8, rect.top - TOOLBAR_H - 12);
  const left = Math.max(8, Math.min(Math.max(8, window.innerWidth - TOOLBAR_W - 8), rect.left + (rect.width / 2) - (TOOLBAR_W / 2)));

  const cur = style || {};
  const set = (k, v) => onChange({ ...cur, [k]: v });
  const toggle = (k) => set(k, !cur[k]);
  const applyPreset = (preset) => onChange({ ...cur, preset: preset.v, fontSize: preset.size, fontWeight: preset.weight });
  const currentSize = cur.fontSize || 16;
  const currentPreset = STYLE_PRESETS.find(p => p.v === cur.preset)?.label || 'Custom';

  return createPortal(
    <div
      ref={cardRef}
      className={`fixed z-[170] ${CARD_CLS} px-2 py-1.5 flex items-center gap-0.5`}
      style={{ top, left, width: TOOLBAR_W, height: TOOLBAR_H }}
      data-testid="text-toolbar"
      data-wb-text-toolbar="1"
      data-wb-overlay="1"
    >
      {/* Style preset */}
      <div className="relative">
        <Pill onClick={() => setOpen(open === 'style' ? null : 'style')} testid="text-style-toggle" title="Style preset">
          <Type size={12} /> <span className="text-white/90">{currentPreset}</span> <ChevronDown size={11} className="text-white/50" />
        </Pill>
        {open === 'style' && (
          <PopWrap testid="text-style-menu">
            <p className="text-[9px] uppercase tracking-[0.14em] font-bold text-white/40 px-2 pb-1.5">Preset</p>
            {STYLE_PRESETS.map(p => (
              <button
                key={p.v}
                type="button"
                onClick={() => { applyPreset(p); setOpen(null); }}
                data-testid={`text-style-${p.v}`}
                className={`w-full text-left px-3 py-2 rounded-md flex items-center justify-between ${cur.preset === p.v ? 'bg-[#8B5CF6]/15 text-white ring-1 ring-[#8B5CF6]/30' : 'text-white/75 hover:bg-white/[0.06]'}`}
              >
                <span style={{ fontSize: Math.min(p.size, 22), fontWeight: p.weight }}>{p.label}</span>
                <span className="text-[10px] text-white/40">{p.size}px</span>
              </button>
            ))}
          </PopWrap>
        )}
      </div>
      <Divider />

      {/* Font family */}
      <div className="relative">
        <Pill onClick={() => setOpen(open === 'font' ? null : 'font')} testid="text-font-toggle" title="Font family">
          <span className="truncate max-w-[100px] text-white/90">{(cur.fontFamily && cur.fontFamily !== 'inherit') ? cur.fontFamily : 'Font'}</span>
          <ChevronDown size={11} className="text-white/50" />
        </Pill>
        {open === 'font' && (
          <PopWrap testid="text-font-menu">
            <p className="text-[9px] uppercase tracking-[0.14em] font-bold text-white/40 px-2 pb-1.5">Font family</p>
            {FONTS.map(f => (
              <button key={f.v} type="button" onClick={() => { set('fontFamily', f.v); setOpen(null); }}
                data-testid={`text-font-${f.v.replace(/\s+/g, '-').toLowerCase()}`}
                className={`w-full text-left px-3 py-1.5 rounded-md text-[13px] ${cur.fontFamily === f.v ? 'bg-[#8B5CF6]/15 text-white ring-1 ring-[#8B5CF6]/30' : 'text-white/80 hover:bg-white/[0.06]'}`}
                style={{ fontFamily: f.v === 'inherit' ? undefined : f.v }}>
                {f.label}
              </button>
            ))}
          </PopWrap>
        )}
      </div>
      <Divider />

      {/* Size */}
      <button type="button" data-testid="text-size-down" onClick={() => set('fontSize', Math.max(8, currentSize - 1))}
        className="w-7 h-7 grid place-items-center text-white/60 hover:text-white hover:bg-white/[0.06] rounded-md"><Minus size={11} /></button>
      <input
        type="number"
        min={8}
        max={300}
        value={currentSize}
        onChange={(e) => set('fontSize', Math.max(8, Math.min(300, Number(e.target.value) || 16)))}
        className="w-11 h-7 text-center text-[12px] font-semibold bg-white/[0.05] border border-white/10 rounded-md px-1 outline-none focus:border-[#8B5CF6]/60 text-white"
        data-testid="text-size-input"
      />
      <button type="button" data-testid="text-size-up" onClick={() => set('fontSize', Math.min(300, currentSize + 1))}
        className="w-7 h-7 grid place-items-center text-white/60 hover:text-white hover:bg-white/[0.06] rounded-md"><Plus size={11} /></button>
      <div className="relative">
        <Pill onClick={() => setOpen(open === 'size' ? null : 'size')} testid="text-size-toggle" title="Size presets">
          <ChevronDown size={11} className="text-white/50" />
        </Pill>
        {open === 'size' && (
          <PopWrap testid="text-size-menu">
            <p className="text-[9px] uppercase tracking-[0.14em] font-bold text-white/40 px-1 pb-1.5">Size (px)</p>
            <div className="grid grid-cols-3 gap-1">
              {SIZE_PRESETS.map(s => (
                <button key={s} type="button" onClick={() => { set('fontSize', s); setOpen(null); }}
                  data-testid={`text-size-${s}`}
                  className={`px-2 py-1.5 rounded-md text-[12px] font-semibold ${currentSize === s ? 'bg-[#8B5CF6]/15 text-white ring-1 ring-[#8B5CF6]/30' : 'text-white/70 hover:bg-white/[0.06]'}`}>
                  {s}
                </button>
              ))}
            </div>
          </PopWrap>
        )}
      </div>
      <Divider />

      {/* Bold/Italic/Underline/Strike */}
      <Pill active={cur.bold}      onClick={() => toggle('bold')}      testid="text-bold"      title="Bold (Cmd+B)"><Bold size={12} /></Pill>
      <Pill active={cur.italic}    onClick={() => toggle('italic')}    testid="text-italic"    title="Italic (Cmd+I)"><Italic size={12} /></Pill>
      <Pill active={cur.underline} onClick={() => toggle('underline')} testid="text-underline" title="Underline (Cmd+U)"><Underline size={12} /></Pill>
      <Pill active={cur.strike}    onClick={() => toggle('strike')}    testid="text-strike"    title="Strikethrough"><Strikethrough size={12} /></Pill>
      <Divider />

      {/* Color */}
      <div className="relative">
        <Pill onClick={() => setOpen(open === 'color' ? null : 'color')} testid="text-color-toggle" title="Text color">
          <Palette size={12} />
          <span className="w-3.5 h-3.5 rounded-sm ring-1 ring-white/20" style={{ backgroundColor: cur.color || '#FFFFFF' }} />
        </Pill>
        {open === 'color' && (
          <PopWrap testid="text-color-menu">
            <p className="text-[9px] uppercase tracking-[0.14em] font-bold text-white/40 mb-1.5">Color</p>
            <div className="grid grid-cols-8 gap-1 mb-2.5">
              {COLOR_PRESETS.map(c => (
                <button key={c} type="button" onClick={() => { set('color', c); }}
                  data-testid={`text-color-${c}`}
                  className={`w-6 h-6 rounded-md ring-2 transition-transform ${cur.color === c ? 'ring-[#8B5CF6] scale-110' : 'ring-white/10 hover:ring-white/25'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input type="color" value={cur.color || '#000000'} onChange={(e) => set('color', e.target.value)}
                className="w-8 h-8 rounded-md border border-white/10 bg-transparent cursor-pointer" data-testid="text-color-custom" />
              <input
                type="text"
                value={cur.color || ''}
                placeholder="#FFFFFF"
                onChange={(e) => set('color', e.target.value)}
                className="flex-1 h-8 text-[12px] bg-white/[0.05] border border-white/10 rounded-md px-2 outline-none focus:border-[#8B5CF6]/60 text-white placeholder-white/30"
                data-testid="text-color-hex"
              />
            </div>
          </PopWrap>
        )}
      </div>
      <Divider />

      {/* Alignment */}
      <Pill active={cur.align === 'left'}    onClick={() => set('align', 'left')}    testid="text-align-left"    title="Align left"><AlignLeft size={12} /></Pill>
      <Pill active={cur.align === 'center'}  onClick={() => set('align', 'center')}  testid="text-align-center"  title="Align center"><AlignCenter size={12} /></Pill>
      <Pill active={cur.align === 'right'}   onClick={() => set('align', 'right')}   testid="text-align-right"   title="Align right"><AlignRight size={12} /></Pill>
      <Pill active={cur.align === 'justify'} onClick={() => set('align', 'justify')} testid="text-align-justify" title="Justify"><AlignJustify size={12} /></Pill>
      <Divider />

      {/* Transform + spacing */}
      <div className="relative">
        <Pill onClick={() => setOpen(open === 'transform' ? null : 'transform')} testid="text-transform-toggle" title="Text transform & spacing">
          {cur.transform === 'uppercase' ? <CaseUpper size={12} /> : cur.transform === 'lowercase' ? <CaseLower size={12} /> : <CaseSensitive size={12} />}
          <ChevronDown size={11} className="text-white/50" />
        </Pill>
        {open === 'transform' && (
          <PopWrap testid="text-transform-menu" align="right">
            <p className="text-[9px] uppercase tracking-[0.14em] font-bold text-white/40 px-1 pb-1.5">Case</p>
            {TRANSFORMS.map(t => (
              <button key={t.v} type="button" onClick={() => { set('transform', t.v); setOpen(null); }}
                data-testid={`text-transform-${t.v}`}
                className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-2 ${cur.transform === t.v ? 'bg-[#8B5CF6]/15 text-white ring-1 ring-[#8B5CF6]/30' : 'text-white/75 hover:bg-white/[0.06]'}`}
                title={t.title}>
                <span className="text-[14px] font-bold w-8">{t.label}</span>
                <span className="text-[11px] text-white/50">{t.title}</span>
              </button>
            ))}
            <div className="mt-2 pt-2 border-t border-white/8 space-y-2">
              <NumberRow label="Line Height" value={cur.lineHeight ?? 1.4} step={0.1} min={0.8} max={3} onChange={(v) => set('lineHeight', v)} testid="text-line-height" />
              <NumberRow label="Letter Spacing" value={cur.letterSpacing ?? 0} step={0.5} min={-5} max={20} onChange={(v) => set('letterSpacing', v)} testid="text-letter-spacing" suffix="px" />
            </div>
          </PopWrap>
        )}
      </div>
      <Divider />

      {/* Link */}
      <Pill onClick={async () => {
        const url = await themedPrompt({ message: 'Link URL:', initialValue: cur.link || '' });
        if (url !== null) set('link', url || '');
      }} testid="text-link" title="Add link" active={!!cur.link}>
        <Link2 size={12} />
      </Pill>
    </div>,
    document.body
  );
}

function NumberRow({ label, value, step, min, max, onChange, testid, suffix }) {
  return (
    <div className="flex items-center justify-between gap-2 px-1">
      <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-white/50">{label}</span>
      <div className="flex items-center gap-1">
        <input
          type="number"
          step={step}
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-16 h-7 text-[11px] bg-white/[0.05] border border-white/10 rounded-md px-1 outline-none focus:border-[#8B5CF6]/60 text-right text-white"
          data-testid={testid}
        />
        {suffix && <span className="text-[10px] text-white/40">{suffix}</span>}
      </div>
    </div>
  );
}
